import { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import useAuthModel from "../model/auth";
import { getErrorMessage } from "../utils/errors";
import {
  isValidEmail,
  isValidPassword,
  normalizeEmail,
} from "../utils/validators";

export type RecoverStep = 0 | 1 | 2;

const CODE_LENGTH = 6;
const DEFAULT_PASSWORD = "000000";

// Estado enviado pelo login quando a conta está pendente
interface PendingState {
  pending?: boolean;
  email?: string;
  hashTail?: string;
}

export const ViewModelRecoverPassword = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { requestRecovery, resendCode, verifyRecoveryCode, resetPassword } =
    useAuthModel();

  const initial = (location.state ?? {}) as PendingState;
  // Conta pendente: o código já foi enviado, começa no passo do código
  const pending = Boolean(initial.pending && initial.email);

  const [step, setStep] = useState<RecoverStep>(pending ? 1 : 0);
  const [email, setEmail] = useState(pending ? initial.email! : "");
  const [code, setCode] = useState("");
  // Parte do hash devolvida pela API; o código do email é o prefixo
  const [hashTail, setHashTail] = useState(pending ? initial.hashTail ?? "" : "");
  const [loading, setLoading] = useState(false);
  const [dialog, setDialog] = useState<{
    type: "success" | "error" | null;
    message: string;
  }>(
    pending
      ? {
          type: "success",
          message: `A sua conta está pendente. Enviámos um código de confirmação para ${initial.email}.`,
        }
      : { type: null, message: "" }
  );

  const fail = (message: string) => setDialog({ type: "error", message });

  const run = async (action: () => Promise<void>, fallback: string) => {
    setLoading(true);
    try {
      await action();
    } catch (error: any) {
      fail(getErrorMessage(error, fallback));
    } finally {
      setLoading(false);
    }
  };

  const fullHash = () => code.trim() + hashTail;

  // Passo 1: enviar o código para o email
  const sendCode = async (value: string) => {
    if (!isValidEmail(value)) {
      fail("Insira um email válido. Números de telefone não são aceites.");
      return;
    }
    const normalized = normalizeEmail(value);
    await run(async () => {
      setHashTail(await requestRecovery(normalized));
      setEmail(normalized);
      setCode("");
      setDialog({
        type: "success",
        message: `Enviámos um código de confirmação para ${normalized}.`,
      });
      setStep(1);
    }, "Não foi possível enviar o código.");
  };

  // Reenvia o código do passo 2
  const resend = () =>
    run(async () => {
      setHashTail(await resendCode(email));
      setDialog({
        type: "success",
        message: `Reenviámos o código de confirmação para ${email}.`,
      });
    }, "Não foi possível reenviar o código.");

  // Passo 2: validar o código
  const confirmCode = async (value: string) => {
    if (value.trim().length !== CODE_LENGTH) {
      fail(`O código deve ter ${CODE_LENGTH} caracteres.`);
      return;
    }
    setCode(value.trim());
    await run(async () => {
      await verifyRecoveryCode(email, value.trim() + hashTail);
      setDialog({ type: null, message: "" });
      setStep(2);
    }, "Código de confirmação inválido.");
  };

  // Passo 3: definir a nova senha
  const changePassword = async (password: string, confirm: string) => {
    if (!isValidPassword(password)) {
      fail("A senha deve ter entre 6 e 15 dígitos.");
      return;
    }
    if (pending && password === DEFAULT_PASSWORD) {
      fail("Escolha uma senha diferente da senha padrão.");
      return;
    }
    if (password !== confirm) {
      fail("As senhas não coincidem.");
      return;
    }
    await run(async () => {
      await resetPassword(email, fullHash(), password);
      setDialog({ type: "success", message: "Senha definida com sucesso!" });
      setTimeout(() => navigate("/login"), 1200);
    }, "Não foi possível alterar a senha.");
  };

  // Numa conta pendente não há passo do email para onde voltar
  const firstStep: RecoverStep = pending ? 1 : 0;

  const back = () => {
    setDialog({ type: null, message: "" });
    setStep((s) => (s > firstStep ? ((s - 1) as RecoverStep) : s));
  };

  return {
    step,
    firstStep,
    pending,
    email,
    code,
    loading,
    dialog,
    sendCode,
    confirmCode,
    changePassword,
    resend,
    back,
    CODE_LENGTH,
  };
};
